import { Link } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

/**
 * Cards of the field-staff web home. Each card takes one settled part
 * ({ status: 'loading'|'ok'|'hidden'|'error', ... }) and draws its own state.
 */

// border-solid and the shadow are explicit: the app CSS sets no default border
// style, and its light theme paints bg-white the same gray as the page.
const CARD = 'rounded-xl border border-solid border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800';
const LABEL = 'text-xs font-bold tracking-wider text-gray-600 dark:text-gray-400';
const SUB = 'm-0 text-sm text-gray-600 dark:text-gray-400';
// Lists reset explicitly: a global list style indented the rows on phones.
const LIST = 'm-0 list-none p-0';
// A top rule between rows, each side set: with no CSS reset, a bare
// border-style falls back to the browser's thick default on the other sides.
const RULE = 'border-x-0 border-b-0 border-t border-solid border-gray-200 dark:border-gray-700';
const divided = (index) => (index > 0 ? RULE : '');

const BADGE = {
  done: 'bg-emerald-700 text-emerald-100',
  active: 'bg-amber-600 text-amber-100',
  upcoming: 'bg-blue-700 text-blue-100',
  missed: 'bg-red-700 text-red-100',
};
const COUNT = {
  amber: 'bg-amber-600 text-amber-100',
  red: 'bg-red-700 text-red-100',
  blue: 'bg-blue-700 text-blue-100',
};

function Spinner() {
  return (
    <div role="status" className="flex justify-center py-6">
      <div aria-hidden="true" className="h-7 w-7 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600 dark:border-gray-700 dark:border-t-teal-500" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

/** Loading spinner, or a short failure line; children only when loaded. */
function Body({ status, children }) {
  if (status === 'loading') return <Spinner />;
  if (status === 'error') return <p className={`${SUB} py-3`}>Couldn&apos;t load this. Refresh to try again.</p>;
  return children;
}

export function TodayCard({ status, today }) {
  if (status === 'hidden') return null;
  const pct = today && today.total > 0 ? Math.round((today.done / today.total) * 100) : 0;
  return (
    <section aria-labelledby="fs-today" className={`${CARD} space-y-3`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="fs-today" className={`m-0 ${LABEL}`}>TODAY</h2>
        {today && <span className={SUB}>{`${today.done} of ${today.total} visits done`}</span>}
      </div>
      <Body status={status}>
        <div className="h-2 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
        {today?.rows.length ? (
          <ul className={LIST}>
            {today.rows.map((row, i) => (
              <li key={row.id} className={`flex items-center justify-between gap-3 py-3 ${divided(i)}`}>
                <div className="min-w-0">
                  <p className="m-0 font-semibold text-gray-900 dark:text-white">{row.client}</p>
                  <p className={SUB}>{row.service ? `${row.time} · ${row.service}` : row.time}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${BADGE[row.tone]}`}>{row.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={`${SUB} py-2`}>No visits scheduled today.</p>
        )}
      </Body>
    </section>
  );
}

export function NeedsCard({ status, items }) {
  return (
    <section aria-labelledby="fs-needs" className={`${CARD} space-y-2`}>
      <h2 id="fs-needs" className={`m-0 ${LABEL}`}>NEEDS YOU</h2>
      <Body status={status}>
        {items.length ? (
          <ul className={LIST}>
            {items.map((item, i) => (
              <li key={item.key} className={`flex items-center gap-3 py-3 ${divided(i)}`}>
                <span className={`flex h-8 min-w-10 shrink-0 items-center justify-center rounded-lg px-2 font-extrabold ${COUNT[item.tone]}`}>{item.count}</span>
                <div className="min-w-0">
                  <p className="m-0 font-semibold text-gray-900 dark:text-white">{item.title}</p>
                  <p className={SUB}>{item.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className={`${SUB} py-2`}>You&apos;re all caught up.</p>
        )}
      </Body>
    </section>
  );
}

export function PayPeriodCard({ status, summary }) {
  if (status === 'hidden' || (status === 'ok' && !summary)) return null;
  return (
    <section aria-labelledby="fs-pay" className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="fs-pay" className={`m-0 ${LABEL}`}>THIS PAY PERIOD</h2>
        {summary && <span className={SUB}>{summary.range}</span>}
      </div>
      <Body status={status}>
        {summary && (
          <div className="grid grid-cols-3 gap-3">
            <Stat value={`${summary.hours} h`} label="Worked" />
            <Stat value={summary.visits} label="Visits" />
            <Stat value={summary.daysLeft} label={summary.daysLeft === 1 ? 'Day left' : 'Days left'} />
          </div>
        )}
      </Body>
      <Link to="/admin/time-tracking" className="inline-flex min-h-11 items-center font-semibold text-teal-700 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300">View my time</Link>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="m-0 text-2xl font-extrabold text-gray-900 dark:text-white">{value}</p>
      <p className={SUB}>{label}</p>
    </div>
  );
}

export function ClientsCard({ status, clients }) {
  if (status === 'hidden') return null;
  return (
    <section aria-labelledby="fs-clients" className={`${CARD} space-y-2`}>
      <h2 id="fs-clients" className={`m-0 ${LABEL}`}>MY CLIENTS</h2>
      <Body status={status}>
        {clients.length ? (
          <ul className={LIST}>
            {clients.map((c, i) => (
              <li key={c.id} className={`flex items-center gap-3 py-3 ${divided(i)}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 font-bold text-gray-800 dark:bg-slate-700 dark:text-slate-100">{c.initials}</span>
                <div className="min-w-0">
                  <p className="m-0 font-semibold text-gray-900 dark:text-white">{c.name}</p>
                  <p className={SUB}>{c.next}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className={`${SUB} py-2`}>No clients assigned yet.</p>
        )}
      </Body>
    </section>
  );
}

export function TimeOffCard({ status, summary }) {
  if (status === 'hidden') return null;
  return (
    <section aria-labelledby="fs-off" className={`${CARD} space-y-3`}>
      <h2 id="fs-off" className={`m-0 ${LABEL}`}>TIME OFF</h2>
      <Body status={status}>
        {summary.balances.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {summary.balances.map((b) => (
              <div key={b.label} className="rounded-lg border border-solid border-gray-200 bg-gray-100 p-3 dark:border-gray-700 dark:bg-gray-900">
                <p className="m-0 text-2xl font-extrabold text-gray-900 dark:text-white">{`${b.hours} h`}</p>
                <p className={SUB}>{`${b.label} available`}</p>
              </div>
            ))}
          </div>
        )}
        {summary.pending.map((r) => (
          <div key={r.id} className={`flex flex-wrap items-center justify-between gap-2 pt-3 ${RULE}`}>
            <span className="text-gray-900 dark:text-white">{`${r.label} · ${r.when} · ${r.hours} h`}</span>
            <span className="rounded-full bg-amber-600 px-2.5 py-1 text-xs font-bold text-amber-100">Waiting for approval</span>
          </div>
        ))}
        {!summary.balances.length && !summary.pending.length && <p className={`${SUB} py-2`}>No time off balances yet.</p>}
      </Body>
    </section>
  );
}

export function MessagesCard({ status, summary }) {
  if (status === 'hidden') return null;
  return (
    <section aria-labelledby="fs-msg" className={`${CARD} flex flex-wrap items-center justify-between gap-4`}>
      <div className="flex items-center gap-3">
        <ChatBubbleLeftRightIcon className="h-7 w-7 text-teal-600 dark:text-teal-400" aria-hidden="true" />
        <div>
          <h2 id="fs-msg" className="m-0 text-base font-bold text-gray-900 dark:text-white">Messages</h2>
          <p className={SUB}>
            {status === 'loading' ? 'Checking for new messages' : status === 'error' ? 'Couldn’t check messages' : summary.text}
          </p>
        </div>
      </div>
      <Link to="/admin/communication" className="inline-flex min-h-11 items-center rounded-lg bg-teal-700 px-4 font-semibold text-white hover:bg-teal-800">Open messages</Link>
    </section>
  );
}
